/**
 * Solo Campaign Setup
 *
 * Creation flow for solo play campaigns with AI DM.
 * Collects: adventure name, premise, table style, character selection.
 */

import { useState } from 'react';
import { useCampaignStore } from '../stores/campaignStore';
import { useSessionStore } from '../stores/sessionStore';
import { useSoloGamemasterStore } from '../stores/soloGamemasterStore';
import { Icon } from './Icon';
import type { SoloCampaignAIConfig } from '@machina/core';
import { DEFAULT_SOLO_AI_CONFIG, DEFAULT_CAMPAIGN_PROFILE } from '@machina/core';
import type { CampaignProfile } from '@machina/core';

const TABLE_STYLES: Array<{ id: SoloCampaignAIConfig['tableStyle']; label: string; desc: string }> = [
  { id: 'HEROIC', label: 'Heroic', desc: 'Epic fantasy, dramatic moments, balanced difficulty' },
  { id: 'GRITTY', label: 'Gritty', desc: 'Dark, grounded, consequences matter' },
  { id: 'COMEDIC', label: 'Comedic', desc: 'Lighthearted, funny NPCs, absurd situations' },
  { id: 'FAMILY', label: 'Family', desc: 'Wholesome, gentle stakes, age-appropriate' },
];

const PREMISE_TEMPLATES = [
  'A mysterious merchant offers you a map to a lost dungeon beneath the city.',
  'You wake in a tavern with no memory of the last three days. There is blood on your hands.',
  'The village elder begs you to investigate disappearances in the Whispering Woods.',
  'A dying knight presses a sealed letter into your hands. "Deliver this to the Queen. Trust no one."',
  'Your ship has run aground on an uncharted island. Strange lights flicker in the jungle interior.',
];

interface SoloCampaignSetupProps {
  onComplete: () => void;
  onCancel: () => void;
  onCreated?: (campaignId: string) => void;
}

export function SoloCampaignSetup({ onComplete, onCancel, onCreated }: SoloCampaignSetupProps) {
  const [name, setName] = useState('Solo Adventure');
  const [premise, setPremise] = useState('');
  const [tableStyle, setTableStyle] = useState<SoloCampaignAIConfig['tableStyle']>('HEROIC');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { createCampaign } = useCampaignStore();
  const { userId } = useSessionStore();

  const handleCreate = async () => {
    if (!userId) {
      setError('Not logged in');
      return;
    }

    setIsCreating(true);
    setError(null);

    const aiConfig: SoloCampaignAIConfig = {
      ...DEFAULT_SOLO_AI_CONFIG,
      tableStyle,
      adventurePremise: premise || undefined,
    };

    const profile: CampaignProfile = {
      ...DEFAULT_CAMPAIGN_PROFILE,
    };

    try {
      const campaign = await createCampaign(
        userId,
        name,
        premise || 'A solo adventure with an AI Dungeon Master.',
        profile,
        'solo',
        aiConfig,
      );

      if (campaign) {
        // Set solo mode in the gamemaster store
        useSoloGamemasterStore.getState().setSoloMode(campaign.id);
        onCreated?.(campaign.id);
        onComplete();
      } else {
        setError('Failed to create campaign');
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div style={{
      maxWidth: 520,
      margin: '0 auto',
      padding: 24,
      fontFamily: 'Georgia, serif',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 24,
      }}>
        <Icon name="sparkle" size={20} color="#d4af37" />
        <h2 style={{
          margin: 0,
          color: '#d4af37',
          fontSize: 20,
          fontFamily: 'Georgia, serif',
        }}>
          Solo Adventure
        </h2>
      </div>

      <p style={{
        color: '#a09aac',
        fontSize: 13,
        lineHeight: 1.6,
        marginBottom: 20,
      }}>
        An AI Dungeon Master will narrate your adventure, create encounters,
        run NPCs, and adjudicate dice rolls. You control your character.
      </p>

      {/* Adventure Name */}
      <div style={{ marginBottom: 16 }}>
        <label style={{
          display: 'block',
          fontSize: 12,
          color: '#c0c0c0',
          marginBottom: 4,
          fontFamily: 'system-ui, sans-serif',
        }}>
          Adventure Name
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="My Solo Adventure"
          style={{
            width: '100%',
            padding: '8px 12px',
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(212, 175, 55, 0.3)',
            borderRadius: 4,
            color: '#e8e0f0',
            fontSize: 14,
            fontFamily: 'Georgia, serif',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Table Style */}
      <div style={{ marginBottom: 16 }}>
        <label style={{
          display: 'block',
          fontSize: 12,
          color: '#c0c0c0',
          marginBottom: 8,
          fontFamily: 'system-ui, sans-serif',
        }}>
          Table Style
        </label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {TABLE_STYLES.map((style) => (
            <button
              key={style.id}
              onClick={() => setTableStyle(style.id)}
              style={{
                padding: '6px 14px',
                borderRadius: 4,
                border: `1px solid ${tableStyle === style.id ? '#d4af37' : 'rgba(192, 192, 192, 0.2)'}`,
                background: tableStyle === style.id ? 'rgba(212, 175, 55, 0.15)' : 'transparent',
                color: tableStyle === style.id ? '#d4af37' : '#a09aac',
                fontSize: 12,
                cursor: 'pointer',
                fontFamily: 'system-ui, sans-serif',
              }}
              title={style.desc}
            >
              {style.label}
            </button>
          ))}
        </div>
        <div style={{
          fontSize: 11,
          color: '#888',
          marginTop: 4,
          fontFamily: 'system-ui, sans-serif',
        }}>
          {TABLE_STYLES.find(s => s.id === tableStyle)?.desc}
        </div>
      </div>

      {/* Adventure Premise */}
      <div style={{ marginBottom: 16 }}>
        <label style={{
          display: 'block',
          fontSize: 12,
          color: '#c0c0c0',
          marginBottom: 4,
          fontFamily: 'system-ui, sans-serif',
        }}>
          Adventure Premise <span style={{ color: '#666' }}>(optional)</span>
        </label>
        <textarea
          value={premise}
          onChange={(e) => setPremise(e.target.value)}
          placeholder="Describe the starting scenario, or leave blank for a surprise..."
          rows={3}
          style={{
            width: '100%',
            padding: '8px 12px',
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(212, 175, 55, 0.3)',
            borderRadius: 4,
            color: '#e8e0f0',
            fontSize: 13,
            fontFamily: 'Georgia, serif',
            resize: 'vertical',
            boxSizing: 'border-box',
          }}
        />
        <div style={{
          display: 'flex',
          gap: 6,
          flexWrap: 'wrap',
          marginTop: 6,
        }}>
          {PREMISE_TEMPLATES.map((template, i) => (
            <button
              key={i}
              onClick={() => setPremise(template)}
              style={{
                padding: '3px 8px',
                borderRadius: 3,
                border: '1px solid rgba(192, 192, 192, 0.15)',
                background: 'transparent',
                color: '#888',
                fontSize: 10,
                cursor: 'pointer',
                fontFamily: 'system-ui, sans-serif',
              }}
              title={template}
            >
              {template.slice(0, 40)}...
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div style={{
          color: '#ff6b6b',
          fontSize: 12,
          marginBottom: 12,
          fontFamily: 'system-ui, sans-serif',
        }}>
          {error}
        </div>
      )}

      {/* Actions */}
      <div style={{
        display: 'flex',
        gap: 12,
        justifyContent: 'flex-end',
        marginTop: 20,
      }}>
        <button
          onClick={onCancel}
          style={{
            padding: '8px 20px',
            borderRadius: 4,
            border: '1px solid rgba(192, 192, 192, 0.2)',
            background: 'transparent',
            color: '#a09aac',
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleCreate}
          disabled={isCreating || !name.trim()}
          style={{
            padding: '8px 24px',
            borderRadius: 4,
            border: '1px solid #d4af37',
            background: isCreating ? 'rgba(212, 175, 55, 0.2)' : 'rgba(212, 175, 55, 0.15)',
            color: isCreating ? '#999' : '#d4af37',
            fontSize: 13,
            fontWeight: 600,
            cursor: isCreating ? 'default' : 'pointer',
            fontFamily: 'Georgia, serif',
          }}
        >
          {isCreating ? 'Creating...' : 'Begin Adventure'}
        </button>
      </div>
    </div>
  );
}
